import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  ShieldCheck, Users, UserCog, LineChart, Fingerprint,
  BellRing, LogOut, Menu, X, Wallet, Building2,
  FolderKanban,
  Receipt,
  UserPlus,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './superadmin.css';

const NAV = [
  { to: '/super-admin', exact: true, label: 'Dashboard', Icon: ShieldCheck },
  { to: '/super-admin/users', label: 'Users', Icon: Users },
  { to: '/super-admin/organizations', label: 'Organizations', Icon: Building2 },
  { to: '/super-admin/onboarding', label: 'Onboarding', Icon: UserPlus },
  { to: '/super-admin/projects', label: 'Projects', Icon: FolderKanban },
  { to: '/super-admin/transactions', label: 'Transactions', Icon: Receipt },
  { to: '/super-admin/admins', label: 'Admins', Icon: UserCog },
  { to: '/super-admin/reports', label: 'Analytics & Reports', Icon: LineChart },
  { to: '/super-admin/security', label: 'Audit & Security', Icon: Fingerprint },
];

export default function SuperAdminLayout() {
  const [open, setOpen] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="superadmin-shell text-slate-800">
      <div className="flex min-h-screen">
        <aside
          className="fixed lg:static z-40 h-screen transition-all duration-300 superadmin-glass"
          style={{ width: 290, transform: open ? 'translateX(0)' : 'translateX(-290px)' }}
        >
          <div className="px-5 py-5 border-b border-slate-100 flex items-center justify-between" style={{ background: 'linear-gradient(135deg,#c44569 0%,#cf6a87 100%)' }}>
            <div>
              <p className="superadmin-title text-lg font-bold flex items-center gap-2 text-white">
                <Wallet size={16} className="text-white" />
                EMS
              </p>
              <p className="text-xs text-white/80">Super Admin Panel</p>
            </div>
            <button className="lg:hidden p-1.5 rounded-md bg-white/20 text-white" onClick={() => setOpen(false)}>
              <X size={16} />
            </button>
          </div>

          <div className="px-5 py-4 border-b border-slate-100 bg-[#fff5f8]">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Authorized Identity</p>
            <p className="mt-2 font-semibold text-sm truncate text-slate-800">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          </div>

          <nav className="p-4 space-y-1.5">
            {NAV.map(({ to, exact, label, Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={exact}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all ${
                    isActive
                      ? 'text-white font-semibold shadow-lg'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`
                }
                style={({ isActive }) => isActive ? { background: 'linear-gradient(135deg,#c44569 0%,#cf6a87 100%)' } : {}}
              >
                <Icon size={16} />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-100 bg-white">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-semibold transition"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </aside>

        <div className="flex-1 min-h-screen w-full lg:w-[calc(100%-290px)]">
          <header className="h-16 px-5 lg:px-8 flex items-center justify-between border-b border-slate-100 bg-white shadow-sm">
            <button className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition" onClick={() => setOpen(v => !v)}>
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <BellRing size={14} className="text-[#c44569]" />
              Real-time enterprise monitoring enabled
            </div>
          </header>

          <main className="p-4 lg:p-7 superadmin-scroll overflow-auto bg-slate-50">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
